import React, { FC, Fragment, useMemo } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    IconButton,
    FormControl,
    InputAdornment,
    Tooltip,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { FixedSizeList as List, ListChildComponentProps } from 'react-window';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { PriceListMaterial } from 'models/material';
import MonetaryValueInput from 'components/monetary-value/monetary-value.component';
import MaterialBatcherTooltip from '../shared/material-batcher-tooltip/material-batcher-tooltip.component';
import { useTranslation } from 'react-i18next';
import SortableHeaderLabel from 'components/shared/sortable-header-label/sortable-header-label.component';
import { OrderBy } from 'models/search-result';
import { MATERIAL_PRICE_LIST_SORT_FIELDS } from 'utils/price-list-sort';

type MaterialPriceListsItemProps = {
    materials: PriceListMaterial[];
    onSaveMaterialPrice: (material: PriceListMaterial) => void;
    onEditMaterialPrice: (material: PriceListMaterial) => void;
    onRemoveMaterialPrice: (material: PriceListMaterial) => void;
    currency: string;
    conversionCurrency?: string;
    isCurrencyConverted: boolean;
    addPriceLoading: boolean;
    editPriceLoading: boolean;
    orderBy: OrderBy;
    onSortChange: (field: string) => void;
};

const ROW_HEIGHT = 80;

const MaterialPriceListsItem: FC<MaterialPriceListsItemProps> = ({
    materials,
    onSaveMaterialPrice,
    onEditMaterialPrice,
    onRemoveMaterialPrice,
    currency,
    conversionCurrency,
    isCurrencyConverted,
    addPriceLoading,
    editPriceLoading,
    orderBy,
    onSortChange,
}: MaterialPriceListsItemProps) => {
    const { t: translate } = useTranslation(['actions-materials', 'common']);

    const Row: FC<ListChildComponentProps> = ({ index, style }) => {
        const material = materials[index];
        const theme = useTheme();
        const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
        const [savingSpinner, setSavingSpinner] = React.useState(false);

        const handleBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
            const value = event.target.value.replace(',', '.');
            if ((!value && !material.basePrice) || value == material.basePrice) {
                return;
            } else if (!value && material.basePrice) {
                onRemoveMaterialPrice(material);
            } else if ((value && material.basePrice) || material.basePrice?.toString() === '0') {
                onEditMaterialPrice({ ...material, basePrice: value });
            } else {
                onSaveMaterialPrice({ ...material, basePrice: value });
            }

            setSavingSpinner(true);
            setTimeout(() => {
                if (!addPriceLoading || !editPriceLoading) setSavingSpinner(false);
            }, 500);
        };

        return (
            <Box
                style={style}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: isSmall ? 'wrap' : 'nowrap',
                    gap: isSmall ? 1 : 0,
                    px: 1,
                    py: 0.5,
                    borderBottom: '1px solid rgba(246, 246, 246, 1)',
                }}
            >
                <Box width={'10%'}>
                    <Typography variant="body2">{material.materialCode}</Typography>
                </Box>

                <Box width={'45%'}>
                    <Typography variant="body2">{material.description}</Typography>
                </Box>

                <Box width={'10%'}>
                    <MaterialBatcherTooltip material={material} />
                </Box>

                <Box width={'15%'}>
                    {isCurrencyConverted && (
                        <FormControl fullWidth margin="none">
                            <MonetaryValueInput
                                size="small"
                                disabled
                                value={parseFloat(material.originalPrice || '0')
                                    .toFixed(2)
                                    .replace('.', ',')}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">{conversionCurrency}</InputAdornment>,
                                }}
                            />
                        </FormControl>
                    )}
                </Box>

                <Box width={'2.5%'}>
                    {!isCurrencyConverted && (material.basePrice || material.basePrice == '0') && (
                        <CheckCircleIcon sx={{ marginTop: '10px' }} color="success" />
                    )}
                </Box>

                <Box width={'15%'}>
                    <FormControl fullWidth margin="none">
                        <MonetaryValueInput
                            size="small"
                            disabled={isCurrencyConverted}
                            value={
                                material.basePrice &&
                                parseFloat(material.basePrice || '0')
                                    .toFixed(2)
                                    .replace('.', ',')
                            }
                            onBlur={handleBlur}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {savingSpinner ? <CircularProgress size={18} /> : currency}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </FormControl>
                </Box>

                <Box width={'2.5%'}>
                    {!isCurrencyConverted && (material.basePrice || material.basePrice == '0') ? (
                        <Tooltip title="Remove material">
                            <IconButton onClick={() => onRemoveMaterialPrice(material)} size="small">
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    ) : undefined}
                </Box>
            </Box>
        );
    };

    const MemoizedRow = useMemo(() => Row, [materials]);

    return (
        <Fragment>
            <Box display="flex" px={1} py={0.5} sx={{ borderBottom: '2px solid', borderColor: 'focus.light' }}>
                <Box width={'10%'}>
                    <SortableHeaderLabel
                        field={MATERIAL_PRICE_LIST_SORT_FIELDS.MATERIAL_CODE}
                        label={translate('actions-materials:CODE')}
                        orderBy={orderBy}
                        onSortChange={onSortChange}
                    />
                </Box>
                <Box width={'45%'}>
                    <SortableHeaderLabel
                        field={MATERIAL_PRICE_LIST_SORT_FIELDS.DESCRIPTION}
                        label={translate('common:DESCRIPTION')}
                        orderBy={orderBy}
                        onSortChange={onSortChange}
                    />
                </Box>
                <Box width={'10%'}>
                    <Typography variant="subtitle2">{translate('actions-materials:OPEN_BATCHES')}</Typography>
                </Box>
                <Box width={'15%'}>
                    {isCurrencyConverted && (
                        <Typography variant="subtitle2">{translate('common:ORIGINAL_PRICE')}</Typography>
                    )}
                </Box>
                <Box width={'2.5%'} />
                <Box width={'15%'}>
                    <Typography variant="subtitle2">{translate('common:BASE_PRICE')}</Typography>
                </Box>
                <Box width={'2.5%'} />
            </Box>
            <List height={600} itemCount={materials.length} itemSize={ROW_HEIGHT} width="100%">
                {MemoizedRow}
            </List>
        </Fragment>
    );
};

export default MaterialPriceListsItem;
